import React, { FC, useEffect, useMemo, useState } from 'react'
import { useLoading } from '../../../Provider/LoadingProvider'
import { Image } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import { IconButton } from '@mui/material'
import Button from '../../../components/Form/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Update,
  CheckCircle,
  Cancel,
  DisabledByDefaultOutlined,
  IndeterminateCheckBoxOutlined,
  CheckBoxOutlined
} from '@mui/icons-material'
import { getPaymentDetail, processPayment } from '../../../services/Payments'
import './style.scss'
import { details, PaymentDetails } from '../../../types/Payments'
import { faCloud } from '@fortawesome/free-solid-svg-icons'

const PaymentsDetails: FC<{ PaymentId: string; onClose: () => void }> = ({
  PaymentId,
  onClose
}) => {
  const { startLoading, stopLoading } = useLoading()
  const [data, setData] = useState<PaymentDetails | undefined>(undefined)
  const [totalPayment, setTotalPayment] = useState<number | undefined>(undefined)
  const [considerStatus, setConsiderStatus] = useState(false) // Controla si se considera el status
  const [statustotal, setstatustotal] = useState<'Payment' | 'Process' | 'Reject'>('Process') // Controla si se considera el status

  useEffect(() => {
    getData()
  }, [])

  useMemo(() => {
    setTotalPayment(
      data
        ? data.details.reduce((total, detail) => {
            // Si no se debe considerar el status, suma todos los detalles
            if (!considerStatus) {
              return total + detail.price
            }
            // Si se considera el status, solo suma aquellos con status 'Payment'
            return detail.status === 'Payment' ? total + detail.price : total
          }, 0)
        : 0
    )
  }, [data, considerStatus])

  const getData = async () => {
    try {
      startLoading()
      const statement = await getPaymentDetail(PaymentId)
      setData(statement)
    } catch (error) {
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const precess = async (
    idPayment: string,
    details: details[],
    type: 'approve' | 'partially' | 'decline'
  ) => {
    try {
      startLoading()
      await processPayment(idPayment, type, details)
      onClose()
    } catch (error) {
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const statusSvg: {
    [key: string]: { generalStatus: React.JSX.Element; detailsStatus: React.JSX.Element }
  } = {
    Payment: {
      generalStatus: <CheckCircle style={{ color: 'green' }} />,
      detailsStatus: <CheckBoxOutlined style={{ color: 'green' }} />
    },

    Process: {
      generalStatus: <Update style={{ color: 'yellow' }} />,
      detailsStatus: <IndeterminateCheckBoxOutlined style={{ color: 'antiquewhite' }} />
    },

    Reject: {
      generalStatus: <Cancel style={{ color: 'red' }} />,
      detailsStatus: <DisabledByDefaultOutlined style={{ color: 'red' }} />
    }
  }

  const handleStatus = (index: number) => {
    // Copia el estado actual de los detalles
    const updatedDetails = [...(data?.details || [])]

    // Obtén el estado actual del detalle
    const currentStatus = updatedDetails[index].status

    // Define el orden en que los estados cambiarán
    const statusOrder = ['Payment', 'Reject'] as const

    // Encuentra el índice del estado actual en el arreglo statusOrder
    const currentStatusIndex = statusOrder.indexOf(currentStatus)

    // Calcula el siguiente estado (cíclico)
    const nextStatusIndex = (currentStatusIndex + 1) % statusOrder.length
    const nextStatus = statusOrder[nextStatusIndex]

    // Actualiza el estado del detalle
    updatedDetails[index].status = nextStatus

    // Actualiza el estado global con el nuevo arreglo de detalles
    setData(prevData => (prevData ? { ...prevData, details: updatedDetails } : undefined))

    setConsiderStatus(true)
  }

  const handleStatusGeneral = () => {
    // Copia el estado actual de los detalles
    const updatedDetails = [...(data?.details || [])]

    // Encuentra el índice del estado actual en el arreglo (asegúrate de definir 'statustotal')
    const statusOrder = ['Payment', 'Reject'] as const // 'as const' asegura que sea un tuple con valores literales
    const currentStatusIndex =
      statustotal === 'Process' ? 0 : statusOrder.indexOf(statustotal as 'Payment' | 'Reject')

    // Calcula el siguiente estado (cíclico)
    const nextStatusIndex = (currentStatusIndex + 1) % statusOrder.length
    const nextStatus = statusOrder[nextStatusIndex] // El tipo será "Payment" | "Reject" por 'as const'

    // Mapea los detalles para actualizar el estado
    const newDetails = updatedDetails.map(detail => {
      return { ...detail, status: nextStatus } // 'nextStatus' ahora es "Payment" | "Reject"
    })

    // Actualiza el estado global con el nuevo arreglo de detalles
    setData(prevData => (prevData ? { ...prevData, details: newDetails } : undefined))
    setstatustotal(nextStatus)
    setConsiderStatus(true)
  }

  return (
    <div className="paymentsDetail-container">
      {data && (
        <>
          <div className="containerphoto">
            <div className="photo">
              <Image
                preview={{
                  mask: (
                    <div>
                      <EyeOutlined style={{ marginRight: '5px' }} />
                      <span>Ver</span>
                    </div>
                  )
                }}
                wrapperStyle={{ width: '100%', height: '100%' }}
                style={{ width: '100%', height: '100%' }}
                src={data.photo}
                className="image"
              />
            </div>
          </div>

          <div className="inf">
            <div className="database-container">
              <div className="data">
                <p>ACREDITÓ {data.date}</p>

                <p>{data.residentname}</p>

                <p>{data.clienName}</p>

                <p>ESTADO DE CUENTA - {data.datePayment}</p>
              </div>
              <div className="status">{statusSvg[data.status].generalStatus}</div>
            </div>

            <div className="statement">
              {data.details.map((details, index) => (
                <div key={index} className="detail">
                  <span>{details.type}</span>
                  <span>{details.concept}</span>
                  <span>{details.dateCreate}</span>
                  <span>Q {details.price}</span>
                  <IconButton
                    onClick={() => {
                      handleStatus(index)
                    }}
                  >
                    {statusSvg[details.status].detailsStatus}
                  </IconButton>
                </div>
              ))}
            </div>
            <div className="totalStatement">
              <div>SALDO AL CORTE</div>
              <div>
                <span>Q {totalPayment}</span>
                <IconButton
                  onClick={() => {
                    handleStatusGeneral()
                  }}
                >
                  {statusSvg[statustotal].detailsStatus}
                </IconButton>
              </div>
            </div>
            <div className="actions">
              <Button
                text="Confirmar todo"
                styleType={`normal`}
                onClick={() => precess(data.idPayment, data.details, 'approve')}
                icon={<FontAwesomeIcon style={{ marginRight: '10px' }} icon={faCloud} />}
              />
              <Button
                text="Confirmar parcial"
                styleType={`normal`}
                onClick={() => precess(data.idPayment, data.details, 'partially')}
                icon={<FontAwesomeIcon style={{ marginRight: '10px' }} icon={faCloud} />}
              />
              <Button
                text="Rechazo total"
                styleType={`normal`}
                onClick={() => precess(data.idPayment, data.details, 'decline')}
                icon={<FontAwesomeIcon style={{ marginRight: '10px' }} icon={faCloud} />}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default PaymentsDetails
